/**
 * Gym Today — mobile-first logger over the day's planned session.
 * One card per exercise; a set confirms with one tap (actuals default to
 * targets server-side), or expands for quick stepper edits. Weights display
 * in lbs, storage stays kg.
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { usePlanToday, useCompleteSet, useUpdateSession } from "../hooks/useGym";
import type { GymSession, GymSet } from "@black-wall/shared";

const KG_PER_LB = 0.453592;
const toLbs = (kg: number | null | undefined): number =>
  kg == null ? 0 : Math.round(kg / KG_PER_LB);
const toKg = (lbs: number): number => +(lbs * KG_PER_LB).toFixed(3);

const RPE_CHIPS: Array<{ label: string; rpe: number }> = [
  { label: "RIR 3", rpe: 7 },
  { label: "RIR 2", rpe: 8 },
  { label: "RIR 1", rpe: 9 },
  { label: "Fallo", rpe: 10 },
];

function isCardio(set: GymSet): boolean {
  return set.target_duration_seconds != null || set.duration_seconds != null;
}

interface ExerciseGroup {
  exerciseId: number;
  name: string;
  sets: GymSet[];
}

function groupSets(sets: GymSet[]): ExerciseGroup[] {
  const groups: ExerciseGroup[] = [];
  const byId = new Map<number, ExerciseGroup>();
  for (const s of sets) {
    let g = byId.get(s.exercise_id);
    if (!g) {
      g = { exerciseId: s.exercise_id, name: s.exercise_name ?? "?", sets: [] };
      byId.set(s.exercise_id, g);
      groups.push(g);
    }
    g.sets.push(s);
  }
  return groups;
}

function SetEditor({
  set,
  onConfirm,
  onSkip,
  pending,
}: {
  set: GymSet;
  onConfirm: (data: Record<string, number | string>) => void;
  onSkip: () => void;
  pending: boolean;
}) {
  const cardio = isCardio(set);
  const [lbs, setLbs] = useState(toLbs(set.target_weight_kg ?? set.weight_kg));
  const [reps, setReps] = useState(set.target_reps ?? set.reps ?? 0);
  const [rpe, setRpe] = useState<number | null>(null);
  const [minutes, setMinutes] = useState(
    Math.round((set.target_duration_seconds ?? set.duration_seconds ?? 0) / 60),
  );
  const [avgHr, setAvgHr] = useState<string>("");

  const stepBtn =
    "w-12 h-12 rounded border border-neutral-700 text-lg font-mono active:bg-neutral-800";

  if (cardio) {
    return (
      <div className="p-3 space-y-3 border-t border-neutral-800">
        <div className="flex items-center gap-3">
          <span className="text-xs text-neutral-500 w-16">min</span>
          <button className={stepBtn} onClick={() => setMinutes((m) => Math.max(0, m - 5))}>
            −5
          </button>
          <span className="flex-1 text-center text-2xl font-mono">{minutes}</span>
          <button className={stepBtn} onClick={() => setMinutes((m) => m + 5)}>
            +5
          </button>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-neutral-500 w-16">lpm prom</span>
          <input
            type="number"
            inputMode="numeric"
            value={avgHr}
            onChange={(e) => setAvgHr(e.target.value)}
            placeholder="130"
            className="flex-1 bg-neutral-900 border border-neutral-700 rounded px-3 py-3 text-center text-2xl font-mono"
          />
        </div>
        <div className="flex gap-2">
          <button
            disabled={pending}
            onClick={() => {
              const data: Record<string, number | string> = {
                duration_seconds: minutes * 60,
              };
              if (avgHr) data.avg_hr = parseInt(avgHr);
              onConfirm(data);
            }}
            className="flex-1 py-3 rounded bg-emerald-900/60 border border-emerald-700 text-emerald-300 font-semibold active:bg-emerald-800"
          >
            Registrar
          </button>
          <button
            disabled={pending}
            onClick={onSkip}
            className="px-4 py-3 rounded border border-neutral-700 text-neutral-500"
          >
            Saltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-3 border-t border-neutral-800">
      <div className="flex items-center gap-3">
        <span className="text-xs text-neutral-500 w-16">lbs</span>
        <button className={stepBtn} onClick={() => setLbs((w) => Math.max(0, w - 5))}>
          −5
        </button>
        <span className="flex-1 text-center text-2xl font-mono">{lbs}</span>
        <button className={stepBtn} onClick={() => setLbs((w) => w + 5)}>
          +5
        </button>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-neutral-500 w-16">reps</span>
        <button className={stepBtn} onClick={() => setReps((r) => Math.max(0, r - 1))}>
          −1
        </button>
        <span className="flex-1 text-center text-2xl font-mono">{reps}</span>
        <button className={stepBtn} onClick={() => setReps((r) => r + 1)}>
          +1
        </button>
      </div>
      <div className="flex gap-2">
        {RPE_CHIPS.map((c) => (
          <button
            key={c.rpe}
            onClick={() => setRpe(rpe === c.rpe ? null : c.rpe)}
            className={`flex-1 py-2 rounded border text-xs ${
              rpe === c.rpe
                ? "border-emerald-600 text-emerald-300 bg-emerald-900/40"
                : "border-neutral-700 text-neutral-400"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <button
          disabled={pending}
          onClick={() => {
            const data: Record<string, number | string> = {
              weight_kg: toKg(lbs),
              reps,
            };
            if (rpe != null) data.rpe = rpe;
            onConfirm(data);
          }}
          className="flex-1 py-3 rounded bg-emerald-900/60 border border-emerald-700 text-emerald-300 font-semibold active:bg-emerald-800"
        >
          Registrar
        </button>
        <button
          disabled={pending}
          onClick={onSkip}
          className="px-4 py-3 rounded border border-neutral-700 text-neutral-500"
        >
          Saltar
        </button>
      </div>
    </div>
  );
}

function SetLine({ set }: { set: GymSet }) {
  const cardio = isCardio(set);
  const done = set.status === "done";
  const skipped = set.status === "skipped";
  const target = cardio
    ? `${Math.round((set.target_duration_seconds ?? 0) / 60)} min`
    : `${toLbs(set.target_weight_kg)} lb × ${set.target_reps ?? "—"}`;
  const actual = cardio
    ? `${Math.round((set.duration_seconds ?? 0) / 60)} min${set.avg_hr ? ` · ${set.avg_hr} lpm` : ""}`
    : `${toLbs(set.weight_kg)} lb × ${set.reps ?? "—"}${set.rpe === 10 ? " · fallo" : set.rpe ? ` · RPE ${set.rpe}` : ""}`;
  return (
    <span
      className={
        skipped
          ? "line-through text-neutral-600"
          : done
            ? "text-emerald-300"
            : "text-neutral-200"
      }
    >
      {done || skipped ? actual : target}
      {set.set_type === "warmup" && (
        <span className="ml-2 text-[10px] uppercase text-amber-500/80">warmup</span>
      )}
    </span>
  );
}

export function GymTodayRoute() {
  const { data, isLoading } = usePlanToday();
  const completeSet = useCompleteSet();
  const updateSession = useUpdateSession();
  const [expandedSet, setExpandedSet] = useState<number | null>(null);
  const [lastConfirmAt, setLastConfirmAt] = useState<number | null>(null);
  const [finishing, setFinishing] = useState(false);

  const session = data && !("error" in data) ? (data as GymSession) : null;
  const groups = useMemo(() => groupSets(session?.sets ?? []), [session]);

  const totals = useMemo(() => {
    const sets = session?.sets ?? [];
    const done = sets.filter((s) => s.status === "done" || s.status === "skipped").length;
    return { done, total: sets.length };
  }, [session]);

  const confirm = (set: GymSet, body: Record<string, number | string | boolean>) => {
    completeSet.mutate({ setId: set.id, data: body });
    setExpandedSet(null);
    setLastConfirmAt(Date.now());
  };

  if (isLoading) {
    return <div className="p-6 text-neutral-500 font-mono text-sm">cargando plan…</div>;
  }

  if (!session) {
    return (
      <div className="p-6 max-w-md mx-auto text-center space-y-4 mt-16">
        <div className="text-neutral-400 text-lg">No hay plan para hoy</div>
        <p className="text-sm text-neutral-600">
          Pide tu sesión en el chat y aparecerá aquí con pesos, warmups y targets listos para
          confirmar set por set.
        </p>
        <Link
          to="/chat"
          search={{ agent: undefined, session: undefined }}
          className="inline-block px-6 py-3 rounded border border-emerald-700 text-emerald-300 bg-emerald-900/30"
        >
          Ir al chat
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto pb-28">
      <header className="p-4 sticky top-0 bg-black/90 backdrop-blur border-b border-neutral-800 z-10">
        <div className="flex items-baseline justify-between">
          <h1 className="text-lg font-semibold text-neutral-100">
            {session.title ?? "Sesión de hoy"}
          </h1>
          <span className="font-mono text-sm text-neutral-500">
            {totals.done}/{totals.total}
          </span>
        </div>
        {session.notes && <p className="text-xs text-neutral-500 mt-1">{session.notes}</p>}
        <RestTimer since={lastConfirmAt} />
      </header>

      <div className="p-3 space-y-3">
        {groups.map((g) => (
          <div key={g.exerciseId} className="rounded border border-neutral-800 bg-neutral-950">
            <div className="px-3 py-2 border-b border-neutral-800/60 text-sm font-semibold text-neutral-300">
              {g.name}
            </div>
            {g.sets.map((s) => (
              <div key={s.id} className="border-b border-neutral-900 last:border-b-0">
                <div className="flex items-center px-3 py-2 gap-2">
                  <span className="font-mono text-xs text-neutral-600 w-5">{s.set_number}</span>
                  <button
                    className="flex-1 text-left text-sm py-1"
                    onClick={() => setExpandedSet(expandedSet === s.id ? null : s.id)}
                  >
                    <SetLine set={s} />
                  </button>
                  {s.status === "planned" && (
                    <button
                      disabled={completeSet.isPending}
                      onClick={() => confirm(s, {})}
                      aria-label="Confirmar set como planeado"
                      className="w-11 h-11 rounded border border-emerald-800 text-emerald-400 text-lg active:bg-emerald-900/50"
                    >
                      ✓
                    </button>
                  )}
                </div>
                {expandedSet === s.id && s.status !== "skipped" && (
                  <SetEditor
                    set={s}
                    pending={completeSet.isPending}
                    onConfirm={(body) => confirm(s, body)}
                    onSkip={() => confirm(s, { skipped: true })}
                  />
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

      {session.status === "active" && !session.ended_at && (
        <footer className="fixed bottom-0 inset-x-0 p-3 bg-black/95 border-t border-neutral-800">
          <div className="max-w-md mx-auto">
            {finishing ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-neutral-500">Energía:</span>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    onClick={() => {
                      updateSession.mutate({
                        id: session.id,
                        data: { ended_at: Date.now(), energy_level: n },
                      });
                      setFinishing(false);
                    }}
                    className="flex-1 py-3 rounded border border-neutral-700 font-mono active:bg-neutral-800"
                  >
                    {n}
                  </button>
                ))}
              </div>
            ) : (
              <button
                onClick={() => setFinishing(true)}
                className="w-full py-3 rounded border border-red-900 text-red-400 bg-red-950/40 font-semibold"
              >
                Terminar sesión
              </button>
            )}
          </div>
        </footer>
      )}
    </div>
  );
}

/** Elapsed time since the last confirmed set — the between-sets rest clock. */
function RestTimer({ since }: { since: number | null }) {
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  if (!since) return null;
  const secs = Math.floor((Date.now() - since) / 1000);
  const mm = Math.floor(secs / 60);
  const ss = String(secs % 60).padStart(2, "0");
  return (
    <div className="mt-1 font-mono text-xs text-cyan-400/80">
      descanso {mm}:{ss}
    </div>
  );
}
